import { useEffect, useMemo, useState } from 'react'
import { Copy, Loader2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { permissionsApi } from '@/api/permissions'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { ApiDepartment } from '@/types/department'

interface DepartmentPermissionCopyDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	sourceDepartmentId: number | null
	departments: ApiDepartment[]
	onCopied?: (targetIds: number[]) => void
}

interface FlatDepartment {
	id: number
	name: string
	level: number
}

function flattenDepartments(departments: ApiDepartment[], level = 0): FlatDepartment[] {
	return departments.flatMap((department) => [
		{
			id: department.id,
			name: department.name,
			level,
		},
		...flattenDepartments(department.children || [], level + 1),
	])
}

export function DepartmentPermissionCopyDialog({
	open,
	onOpenChange,
	sourceDepartmentId,
	departments,
	onCopied,
}: DepartmentPermissionCopyDialogProps) {
	const [keyword, setKeyword] = useState('')
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
	const [copying, setCopying] = useState(false)

	const flatDepartments = useMemo(() => flattenDepartments(departments), [departments])
	const sourceDepartment = flatDepartments.find((department) => department.id === sourceDepartmentId)
	const targetDepartments = flatDepartments.filter((department) => department.id !== sourceDepartmentId)
	const filteredDepartments = targetDepartments.filter((department) =>
		department.name.toLowerCase().includes(keyword.trim().toLowerCase())
	)
	const filteredSelectedCount = filteredDepartments.filter((department) => selectedIds.has(department.id)).length
	const allFilteredSelected = filteredDepartments.length > 0 && filteredSelectedCount === filteredDepartments.length

	useEffect(() => {
		if (!open) {
			setKeyword('')
			setSelectedIds(new Set())
		}
	}, [open])

	const toggleDepartment = (departmentId: number) => {
		setSelectedIds((current) => {
			const next = new Set(current)
			if (next.has(departmentId)) {
				next.delete(departmentId)
			} else {
				next.add(departmentId)
			}
			return next
		})
	}

	const toggleFilteredDepartments = (checked: boolean) => {
		setSelectedIds((current) => {
			const next = new Set(current)
			filteredDepartments.forEach((department) => {
				if (checked) {
					next.add(department.id)
				} else {
					next.delete(department.id)
				}
			})
			return next
		})
	}

	const handleCopy = async () => {
		if (!sourceDepartmentId) {
			toast.error('请先选择要复制的源部门')
			return
		}

		const targetIds = Array.from(selectedIds)
		if (targetIds.length === 0) {
			toast.error('请选择要覆盖权限的目标部门')
			return
		}

		try {
			setCopying(true)
			const sourcePermissions = await permissionsApi.getDepartmentPermissions(sourceDepartmentId)
			for (const departmentId of targetIds) {
				await permissionsApi.setDepartmentPermissions(departmentId, {
					permissions: sourcePermissions.permissions,
				})
			}
			toast.success(`已复制到 ${targetIds.length} 个部门`)
			onCopied?.(targetIds)
			onOpenChange(false)
		} catch (error) {
			console.error('复制部门权限失败:', error)
			toast.error('复制权限失败')
		} finally {
			setCopying(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={copying ? undefined : onOpenChange}>
			<DialogContent className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
				<DialogHeader className="shrink-0 border-b px-6 py-5">
					<DialogTitle className="flex items-center gap-2">
						<Copy className="h-5 w-5" />
						复制部门权限
					</DialogTitle>
					<DialogDescription>
						将「{sourceDepartment?.name || '当前部门'}」的权限全量覆盖到所选部门
					</DialogDescription>
				</DialogHeader>

				<div className="shrink-0 space-y-3 border-b px-6 py-4">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={keyword}
							onChange={(event) => setKeyword(event.target.value)}
							placeholder="搜索目标部门"
							className="pl-9"
						/>
					</div>
					<div className="flex items-center justify-between gap-3 text-sm">
						<label className="flex cursor-pointer items-center gap-2">
							<Checkbox
								checked={allFilteredSelected}
								disabled={filteredDepartments.length === 0}
								onCheckedChange={(checked) => toggleFilteredDepartments(!!checked)}
							/>
							<span>选择当前列表</span>
						</label>
						<span className="text-muted-foreground">已选 {selectedIds.size} 个</span>
					</div>
				</div>

				<div className="min-h-0 flex-1 overflow-auto px-3 py-2">
					{filteredDepartments.length === 0 ? (
						<div className="px-3 py-8 text-center text-sm text-muted-foreground">
							暂无可选择部门
						</div>
					) : (
						filteredDepartments.map((department) => (
							<label
								key={department.id}
								className={cn(
									'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted',
									selectedIds.has(department.id) && 'bg-primary/10 text-primary'
								)}
								style={{ paddingLeft: `${department.level * 16 + 12}px` }}
							>
								<Checkbox
									checked={selectedIds.has(department.id)}
									onCheckedChange={() => toggleDepartment(department.id)}
								/>
								<span className="min-w-0 flex-1 truncate font-medium">{department.name}</span>
							</label>
						))
					)}
				</div>

				<DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:gap-0">
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={copying}>
						取消
					</Button>
					<Button type="button" onClick={handleCopy} disabled={copying || selectedIds.size === 0}>
						{copying ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								复制中...
							</>
						) : (
							<>
								<Copy className="mr-2 h-4 w-4" />
								复制权限
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
